// https://mui.com/material-ui/react-link/
import { Link as MuiLink, LinkProps as MuiLinkProps } from "@mui/material";
import { CSSProperties } from "@mui/styles";
import { formatPhone } from "core/model/utils/strings";
import { KEYBOARD_FOCUS_OUTLINE } from "ds_legacy/materials/colors";
import { dp, padding } from "ds_legacy/materials/metrics";
import { FONT_FAMILY, FONT_WEIGHT_BOLD } from "ds_legacy/materials/typography";
import { Link as RouterLink, To } from "react-router-dom";
import { useTranslations } from "translations";

export type LinkV2Props = MuiLinkProps &
  (
    | {
        href?: never;
        to: To;
      }
    | { href: string; to?: never }
  );

const getDefaultSx = (): CSSProperties => ({
  "&:focus-visible": {
    outline: `${dp(2)} solid ${KEYBOARD_FOCUS_OUTLINE}`,
    outlineOffset: "-1.25px",
    textDecoration: "underline",
    textDecorationColor: "inherit",
  },
  boxSizing: "border-box",
  fontFamily: FONT_FAMILY,
  fontWeight: FONT_WEIGHT_BOLD,
  padding: padding(0, 0.25),
});

export type ExternalLinkType = "email" | "website" | "pdf" | "phone";

export const useExternalLinkLabel = () => {
  const translations = useTranslations();

  const detail: Record<ExternalLinkType, string> = {
    email: translations.providersearch.genericAriaLabelEmailLink,
    pdf: translations.providersearch.genericAriaLabelPDFLink,
    phone: translations.providersearch.genericAriaLabelPhoneLink,
    website: translations.providersearch.genericAriaLabelLink,
  };

  return function externalLinkLabel(
    label: string,
    type: ExternalLinkType = "website",
  ) {
    if (type === "phone") {
      return `${detail[type]} - ${formatPhone(label)}`;
    }
    return `${label} - ${detail[type]}`;
  };
};

function LinkV2({ children, href, to, ...props }: LinkV2Props) {
  const defaultSx = getDefaultSx();

  if (to) {
    return (
      <MuiLink
        component={RouterLink}
        to={to}
        underline="hover"
        {...props}
        sx={{ ...defaultSx, ...props.sx }}
      >
        {children}
      </MuiLink>
    );
  }

  return (
    <MuiLink
      href={href}
      rel={props.target === "_blank" ? "noopener noreferrer" : undefined}
      underline="hover"
      {...props}
      sx={{ ...defaultSx, ...props.sx }}
    >
      {children}
    </MuiLink>
  );
}

export default LinkV2;
